import type { AiMode, GoodType } from "@cwb/core";
import type {
  CreateSessionResponse,
  HealthResponse,
  SessionResultResponse,
  SessionSpeed,
  SessionSpeedResponse,
  SnapshotResponse,
  TradeResponse,
} from "@cwb/protocol";

/**
 * Typed HTTP surface (§20.1).
 *
 * Every helper resolves — a network failure, a non-2xx status and a server
 * `{ ok: false }` body all come back as the same discriminated failure, so no
 * call site ever needs a try/catch.
 */

/**
 * `error` stays ENGLISH here. It is the same vocabulary the server refuses in,
 * so `i18n/errors.ts` can hold one table for both and every call site turns it
 * Korean with `translateError()` at the moment it reaches the screen.
 */
export type ApiResult<T> = { ok: true; data: T } | { ok: false; error: string };

const seg = (value: string): string => encodeURIComponent(value);

/** All server paths live here so the routing contract has exactly one home. */
const ROUTES = {
  health: () => "/api/health",
  createSession: () => "/api/sessions",
  snapshot: (sessionId: string) => `/api/sessions/${seg(sessionId)}`,
  marketBuy: (sessionId: string) => `/api/sessions/${seg(sessionId)}/market/buy`,
  marketSell: (sessionId: string) => `/api/sessions/${seg(sessionId)}/market/sell`,
  marketGift: (sessionId: string) => `/api/sessions/${seg(sessionId)}/market/gift`,
  startSession: (sessionId: string) => `/api/sessions/${seg(sessionId)}/start`,
  sessionSpeed: (sessionId: string) => `/api/sessions/${seg(sessionId)}/speed`,
  result: (sessionId: string) => `/api/sessions/${seg(sessionId)}/result`,
} as const;

function errorFrom(payload: unknown, status: number): string {
  if (payload && typeof payload === "object" && "error" in payload) {
    const detail = (payload as { error: unknown }).error;
    if (typeof detail === "string" && detail.length > 0) return detail;
  }
  return `request failed (${status})`;
}

async function send<T>(path: string, method: "GET" | "POST", body?: unknown): Promise<ApiResult<T>> {
  let response: Response;
  try {
    response = await fetch(path, {
      method,
      headers: body === undefined ? undefined : { "content-type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    return { ok: false, error: "the front is unreachable — server offline?" };
  }

  let payload: unknown = null;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }

  if (!response.ok) return { ok: false, error: errorFrom(payload, response.status) };
  if (!payload || typeof payload !== "object") {
    // Wording matches the `unexpected response shape` entry in i18n/errors.ts.
    return { ok: false, error: "unexpected response shape" };
  }
  if ((payload as { ok?: unknown }).ok !== true) {
    return { ok: false, error: errorFrom(payload, response.status) };
  }
  return { ok: true, data: payload as T };
}

// ---------------------------------------------------------------------------
// session
// ---------------------------------------------------------------------------

/**
 * The browser always asks for a held session: the board is built and sent, but
 * no tick runs until {@link startSession}. The briefing is read over the opening
 * position, and a war that moved while it was being read would have spent part
 * of the round on the rules.
 */
export function createSession(
  seed?: string,
  aiMode?: AiMode,
): Promise<ApiResult<CreateSessionResponse>> {
  const body: Record<string, unknown> = { paused: true };
  if (seed) body.seed = seed;
  if (aiMode) body.aiMode = aiMode;
  return send<CreateSessionResponse>(ROUTES.createSession(), "POST", body);
}

/** Let a held session go. Safe to call twice — the server ignores the second. */
export function startSession(sessionId: string): Promise<ApiResult<{ ok: true }>> {
  return send<{ ok: true }>(ROUTES.startSession(sessionId), "POST", {});
}

export function setSessionSpeed(
  sessionId: string,
  speed: SessionSpeed,
): Promise<ApiResult<SessionSpeedResponse>> {
  return send<SessionSpeedResponse>(ROUTES.sessionSpeed(sessionId), "POST", { speed });
}

export function getSnapshot(sessionId: string): Promise<ApiResult<SnapshotResponse>> {
  return send<SnapshotResponse>(ROUTES.snapshot(sessionId), "GET");
}

export function getResult(sessionId: string): Promise<ApiResult<SessionResultResponse>> {
  return send<SessionResultResponse>(ROUTES.result(sessionId), "GET");
}

export function health(): Promise<ApiResult<HealthResponse>> {
  return send<HealthResponse>(ROUTES.health(), "GET");
}

// ---------------------------------------------------------------------------
// market
// ---------------------------------------------------------------------------

/**
 * Most units one trade request may carry, as `MarketTradeRequestSchema` bounds
 * it. A caller that batches clicks into a single request has to split on this
 * line: past it the server answers with a schema refusal, not a smaller fill.
 */
export const MAX_TRADE_QUANTITY = 200;

export function marketBuy(
  sessionId: string,
  orderId: string,
  quantity: number,
  fromProvinceId?: string,
): Promise<ApiResult<TradeResponse>> {
  return send<TradeResponse>(ROUTES.marketBuy(sessionId), "POST", {
    orderId,
    quantity,
    ...(fromProvinceId ? { fromProvinceId } : {}),
  });
}

/**
 * `onCredit` asks the server to fill the whole quantity and book whatever the
 * nation cannot pay against its credit line. Omitted entirely on a cash sale, so
 * the request body of an ordinary trade is unchanged.
 */
/** Cash, then the credit line, then nothing at all — the three rungs of a sale. */
export type SellTerms = "cash" | "credit" | "gift";

export function marketSell(
  sessionId: string,
  orderId: string,
  quantity: number,
  terms: SellTerms = "cash",
  toProvinceId?: string,
): Promise<ApiResult<TradeResponse>> {
  return send<TradeResponse>(ROUTES.marketSell(sessionId), "POST", {
    orderId,
    quantity,
    ...(terms === "credit" ? { onCredit: true } : {}),
    ...(terms === "gift" ? { asGift: true } : {}),
    ...(toProvinceId ? { toProvinceId } : {}),
  });
}

/**
 * A gift answers no order, so it names the province instead. That is what makes
 * it the one move available against a nation that has stopped bidding — and
 * what makes where it lands the player's decision rather than the depot's.
 */
export function brokerGift(
  sessionId: string,
  nationId: string,
  provinceId: string,
  goodType: GoodType,
  quantity: number,
): Promise<ApiResult<TradeResponse>> {
  return send<TradeResponse>(ROUTES.marketGift(sessionId), "POST", {
    nationId,
    provinceId,
    goodType,
    quantity,
  });
}
